import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '../../shared/components/Common';
import Icon from '../../shared/components/Icons';
import { FlowBand } from '../../shared/booking/FlowParts';
import { addDaysIso, fmtDay, formatPrice } from '../../shared/data/common';
import { CHILD_FACTOR, INFANT_FARE, fareFamilies, getFlight } from './data';
import { AirlineLogo, SegmentTimeline } from './FlightSummary';

export default function FlightDetails() {
  const { flightId } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const q = useQuery();
  const from = q.from || 'Delhi';
  const to = q.to || 'Mumbai';
  const date = q.date || addDaysIso(7);
  const pax = { adults: Number(q.adults || q.travellers || 1), children: Number(q.children || 0), infants: Number(q.infants || 0) };
  const flight = getFlight(flightId, from, to);
  const [fareKey, setFareKey] = useState('saver');

  if (!flight) {
    return (
      <div className="container section empty">
        <h2>Flight not available</h2>
        <Link to={`/flights/results${search}`} className="btn btn-primary">Back to results</Link>
      </div>
    );
  }

  const fares = fareFamilies(flight);
  const fare = fares.find((f) => f.key === fareKey);
  const base = fare.price * pax.adults + Math.round(fare.price * CHILD_FACTOR) * pax.children + INFANT_FARE * pax.infants;

  const proceed = () => {
    navigate('/flights/review', {
      state: {
        summary: {
          from, to, date, dep: flight.dep, arr: flight.arr, duration: flight.duration, stops: flight.stops,
          segments: flight.segments, airline: flight.airline, flightNo: flight.flightNo, cabin: q.cabin || 'Economy',
          fareName: fare.name, cancelFee: fare.cancelFee, baggage: { cabin: fare.cabinBag, checkin: fare.checkin }, pax,
        },
        adultFare: fare.price,
        backTo: `/flights/${flight.id}${search}`,
      },
    });
  };

  return (
    <>
      <FlowBand title={`${from} → ${to}`} step={1} back={{ to: `/flights/results${search}`, label: 'Back to flights' }} />
      <div className="container details-layout">
        <div className="details-main">
          <section className="panel">
            <div className="sum-head">
              <div className="airline"><AirlineLogo airline={flight.airline} /><div><h3>{flight.airline.name} · {flight.flightNo}</h3>
                <span className="muted small">{fmtDay(date)} · {flight.stops ? '1 stop' : 'Non-stop'} · {flight.duration} · {flight.aircraft}</span></div></div>
              <span className="tag">{q.cabin || 'Economy'}</span>
            </div>
            <SegmentTimeline segments={flight.segments} airline={flight.airline} date={date} />
          </section>

          <section className="panel">
            <h3>Choose your fare</h3>
            <div className="fare-options">
              {fares.map((f) => (
                <label key={f.key} className={`fare-option ${fareKey === f.key ? 'active' : ''}`}>
                  <input type="radio" name="fare" checked={fareKey === f.key} onChange={() => setFareKey(f.key)} />
                  <div className="fare-option-head">
                    <b>{f.name}</b>{f.tag && <span className="tag">{f.tag}</span>}
                    <span className="price">{formatPrice(f.price)}</span>
                  </div>
                  <ul>
                    <li><Icon name="bag" size={13} /> Cabin {f.cabinBag}, Check-in {f.checkin}</li>
                    <li><Icon name="calendar" size={13} /> Date change: {f.change}</li>
                    <li><Icon name="close" size={13} /> Cancellation: {f.cancel}</li>
                    <li><Icon name="check" size={13} /> {f.meal} · {f.seat}</li>
                  </ul>
                </label>
              ))}
            </div>
          </section>

          <section className="panel">
            <h3>Baggage & fare rules — {fare.name}</h3>
            <table className="data-table">
              <tbody>
                <tr><th>Cabin baggage</th><td>{fare.cabinBag} (1 piece) per adult & child</td></tr>
                <tr><th>Check-in baggage</th><td>{fare.checkin} per adult & child · Infants: 10 kg</td></tr>
                <tr><th>Date change fee</th><td>{fare.change}</td></tr>
                <tr><th>Cancellation fee</th><td>{fare.cancel} (+ ₹299 Trawtel fee)</td></tr>
                <tr><th>Meals & seats</th><td>{fare.meal} · {fare.seat}</td></tr>
              </tbody>
            </table>
            <p className="muted small">Fare rules are set by the airline and may change until the ticket is issued.</p>
          </section>
        </div>

        <aside className="details-side">
          <div className="panel price-card">
            <span className="muted small">Total for {pax.adults + pax.children + pax.infants} traveller(s)</span>
            <b className="price big">{formatPrice(base)}</b>
            <div className="fare-breakdown">
              <div className="summary-line"><span>Adult × {pax.adults}</span><span>{formatPrice(fare.price * pax.adults)}</span></div>
              {pax.children > 0 && <div className="summary-line"><span>Child × {pax.children}</span><span>{formatPrice(Math.round(fare.price * CHILD_FACTOR) * pax.children)}</span></div>}
              {pax.infants > 0 && <div className="summary-line"><span>Infant × {pax.infants}</span><span>{formatPrice(INFANT_FARE * pax.infants)}</span></div>}
              <p className="muted small">Taxes & fees calculated on next step</p>
            </div>
            <button className="btn btn-primary block lg" onClick={proceed}>Continue</button>
          </div>
        </aside>
      </div>
    </>
  );
}
